import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { useImageFallback } from "../useImageFallback";
import { ProductMediaPlaceholder } from "./ProductMediaPlaceholder";

export interface ProductGalleryThumbProps {
  src: string;
  name: string;
  /** 1, 2, 3… — lo que se lee en el rótulo accesible. */
  position: number;
  selected: boolean;
  onSelect: () => void;
}

/**
 * Una miniatura de la galería.
 *
 * ── `aria-pressed`, no un borde ──────────────────────────────────────
 * Las miniaturas son un grupo de opciones excluyentes. Lo que dice cuál
 * está puesta es `aria-pressed`; el borde violeta es la MISMA información
 * dicha en color, y el color solo no basta.
 *
 * ── El rótulo dice de qué imagen se trata ────────────────────────────
 * "Imagen 2 de Colágeno marino" y no "Imagen": cuatro botones llamados
 * igual no se distinguen al recorrer la pantalla con un lector, y la
 * miniatura de dentro no puede ser el rótulo porque puede no haber foto.
 * La imagen va como decorativa por eso mismo.
 */
export function ProductGalleryThumb({
  src,
  name,
  position,
  selected,
  onSelect,
}: ProductGalleryThumbProps) {
  const { t } = useTranslation();
  const { showImage, onError } = useImageFallback(src);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={t("Imagen {{n}} de {{producto}}", { n: position, producto: name })}
      className={cn(
        /*
         * 60 x 60 con radio 12 y 4 px de aire por dentro: las medidas
         * literales del original. El aire es lo que hace que la foto no
         * toque el borde y que la miniatura se lea como una diapositiva.
         */
        "relative h-[60px] w-[60px] overflow-hidden rounded-[12px] bg-white p-1 transition hover:-translate-y-0.5",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ds-cream",
        /*
         * Activa = borde de 2 px en `ink` y sombra media, como el
         * original. El violeta se descartó: dos miniaturas seguidas, una
         * con filo violeta y otra sin él, se leían como "esta está
         * seleccionada y esta es un enlace".
         */
        selected
          ? "border-2 border-ds-ink shadow-ds-card-hv"
          : "border border-ds-border shadow-ds-xs",
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={onError}
          className="h-full w-full rounded-[8px] object-cover"
        />
      ) : (
        <ProductMediaPlaceholder size="sm" />
      )}
    </button>
  );
}
